import { Link, useParams } from "react-router-dom";

import { api, usePoll } from "../api";
import type { IncidentDetail } from "../api";
import { fmtDay, fmtDuration, fmtTime, fmtTimeSec } from "../format";
import { Clip } from "../components/recording";

export function IncidentView() {
  const { incidentId } = useParams();
  const { data: i, error, reload } = usePoll<IncidentDetail>(`/api/incidents/${incidentId}`, 15_000);

  if (error) return <p className="error">{error}</p>;
  if (!i) return <div className="spinner" />;

  const start = new Date(i.started_at).getTime();
  const span = Math.max(new Date(i.last_detection_at).getTime() - start, 60_000);

  async function label(value: string | null) {
    await api.patch(`/api/incidents/${i!.id}`, { user_label: value });
    reload();
  }

  return (
    <section>
      <Link to="/diary" className="small">← Diary</Link>
      <div className="heading">
        <h2>
          {fmtDay(i.started_at)}, {fmtTime(i.started_at)}–
          {i.status === "open" ? "now" : fmtTime(i.last_detection_at)}
        </h2>
        {i.status === "open" && <span className="pill live">Happening</span>}
      </div>
      <p className="muted">
        {fmtDuration(i.started_at, i.last_detection_at)} · {i.detection_count} barks
        {i.peak_dbfs != null ? ` · loudest ${i.peak_dbfs.toFixed(1)} dBFS` : ""}
      </p>

      <div className="card">
        <h3>Timeline</h3>
        <div className="timeline" aria-label="Each mark is one bark">
          {i.detections.map((d, n) => (
            <span key={n} title={fmtTimeSec(d.ts)}
                  style={{ left: `${((new Date(d.ts).getTime() - start) / span) * 100}%` }} />
          ))}
        </div>
        <div className="timeline-axis small muted">
          <span>{fmtTime(i.started_at)}</span>
          <span>{fmtTime(i.last_detection_at)}</span>
        </div>
      </div>

      <div className="card">
        <h3>Recordings</h3>
        {i.clips.length === 0 && <p className="muted">No recordings uploaded for this incident yet.</p>}
        {i.clips.map((c) => (
          <div key={c.id} className="clip-row">
            <span className="small muted">{fmtTimeSec(c.started_at)}</span>
            <Clip url={c.url} durationMs={c.duration_ms} markersMs={c.markers_ms} />
          </div>
        ))}
      </div>

      <div className="card">
        {i.user_label === "not_target" ? (
          <p>
            Hidden from the diary as not the dog.{" "}
            <button className="link" onClick={() => label(null)}>Put it back</button>
          </p>
        ) : (
          <button className="link" onClick={() => label("not_target")}>
            This wasn't the dog — hide it
          </button>
        )}
      </div>
    </section>
  );
}
